import { useEffect, useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import api from '@/api/axios';
import { toast } from 'sonner';
import AdminDashboard from '@/pages/AdminDashboard';
import { SeguimientoCotizaciones } from '@/components/SeguimientoCotizaciones';
import { MisVentas } from '@/components/MisVentas';
import { ClienteBuscador, HistorialCliente } from '@/components/ClienteBuscador';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import {
  Phone, MapPin, FileText, CurrencyDollar, ChartLineUp,
  CheckCircle, Target, Trophy, Calendar, CircleNotch,
  PencilSimple, Trash
} from '@phosphor-icons/react';

const formatMoney = (n) => `Q${Number(n || 0).toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const WeeklyProgressBar = ({ label, pct, done, target }) => {
  const color = pct === null ? 'bg-[var(--text-faint)]'
    : pct >= 100 ? 'bg-emerald-500'
    : pct >= 70 ? 'bg-blue-500'
    : pct >= 40 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div data-testid={`my-${label.toLowerCase()}-progress`}>
      <div className="flex justify-between text-xs mb-1">
        <span className="font-bold text-[var(--text-muted)] uppercase">{label}</span>
        <span className="font-black text-[var(--text)]">{formatMoney(done)} / {formatMoney(target)} · {pct === null ? '—' : `${pct}%`}</span>
      </div>
      <div className="h-2 rounded-full bg-[var(--bg)] overflow-hidden">
        <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${pct === null ? 0 : Math.min(100, pct)}%` }} />
      </div>
    </div>
  );
};

const WeeklyProgressCard = ({ myGoal, weeklyData }) => {
  if (!myGoal || (!myGoal.target_cyh && !myGoal.target_sa)) {
    return (
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-5 mb-6" data-testid="weekly-progress-card-empty">
        <div className="flex items-center gap-2 mb-2">
          <Trophy size={20} weight="bold" className="text-[var(--text-faint)]" />
          <h3 className="text-sm font-black heading-font uppercase tracking-wide">Mi Cumplimiento Ventas (CYH + SA)</h3>
        </div>
        <p className="text-xs text-[var(--text-muted)]">Aún no tienes metas asignadas. Tu supervisor debe configurarlas desde tu ficha.</p>
      </div>
    );
  }
  const t = (weeklyData && weeklyData.totals) || { sales_cyh: 0, sales_sa: 0, sales_total: 0 };
  const targetTotal = (myGoal.target_cyh || 0) + (myGoal.target_sa || 0);
  const pctCyh = myGoal.target_cyh ? Math.round((t.sales_cyh / myGoal.target_cyh) * 1000) / 10 : null;
  const pctSa = myGoal.target_sa ? Math.round((t.sales_sa / myGoal.target_sa) * 1000) / 10 : null;
  const pctTotal = targetTotal ? Math.round((t.sales_total / targetTotal) * 1000) / 10 : null;
  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-5 mb-6" data-testid="weekly-progress-card">
      <div className="flex items-center gap-2 mb-3">
        <Trophy size={20} weight="bold" className="text-[var(--primary)]" />
        <h3 className="text-sm font-black heading-font uppercase tracking-wide">Mi Cumplimiento Ventas (CYH + SA)</h3>
      </div>
      <div className="space-y-3">
        <WeeklyProgressBar label="CYH" pct={pctCyh} done={t.sales_cyh} target={myGoal.target_cyh} />
        <WeeklyProgressBar label="SA" pct={pctSa} done={t.sales_sa} target={myGoal.target_sa} />
        <WeeklyProgressBar label="TOTAL" pct={pctTotal} done={t.sales_total} target={targetTotal} />
        <p className="text-[10px] text-[var(--text-faint)] italic text-center pt-2">
          Los datos los actualiza tu supervisor. Cualquier corrección, pídelos a él.
        </p>
      </div>
    </div>
  );
};

const ACTIVITY_META = {
  call: { label: 'Llamada', icon: Phone, color: '#6C3FB5', bg: 'bg-[#6C3FB5]/10', border: 'border-[#6C3FB5]/30', text: 'text-[#8B5CF6]' },
  visit: { label: 'Visita', icon: MapPin, color: '#F59E0B', bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400' },
  quote: { label: 'Cotización', icon: FileText, color: '#A855F7', bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-400' },
  sale: { label: 'Venta', icon: CurrencyDollar, color: '#10B981', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400' },
};

const OUTCOMES = {
  call: ['Contestó', 'No contestó', 'Interesado', 'Solicita cotización', 'Volver a llamar', 'No interesado'],
  visit: ['Interesado', 'Solicita cotización', 'Negociación', 'Venta', 'Pendiente', 'No interesado'],
  quote: ['Pendiente', 'Aceptada', 'Rechazada'],
  sale: ['Contado', 'Crédito', 'Anticipo'],
};

const ESTADO_LABEL = {
  cotizada: 'Cotizada',
  aceptada: 'Aceptada',
  planeando: 'Planeando',
  produciendo: 'Produciendo',
  instalando: 'Instalando',
  entregada: 'Entregada',
  cerrada: 'Cerrada',
  cancelada: 'Cancelada',
  anulada: 'Anulada',
};

const ESTADO_COLOR = {
  cotizada: 'bg-gray-500/15 text-gray-400 border-gray-500/30',
  aceptada: 'bg-blue-500/15 text-[#8B5CF6] border-blue-500/30',
  planeando: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/30',
  produciendo: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  instalando: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  entregada: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  cerrada: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
};

const KIND_META = {
  opened: { icon: '🆕', label: 'OT abierta', color: 'text-[#8B5CF6]' },
  status_changed: { icon: '🔄', label: 'Estado actualizado', color: 'text-amber-400' },
  comments: { icon: '💬', label: 'Seguimiento', color: 'text-purple-400' },
  other: { icon: '📌', label: 'Otro', color: 'text-[var(--text-muted)]' },
};

const ProduccionActivityCard = ({ data }) => {
  const today = data.today || {};
  const ots = data.assigned_ots || [];
  const events = data.my_events || [];
  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-5 mb-6" data-testid="mi-dia-produccion-card">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-lg">🏗️</span>
        <h3 className="text-sm font-black heading-font uppercase tracking-wide">Mi actividad en Producción</h3>
        <span className="ml-auto text-[10px] text-[var(--text-muted)]">últimos {data.days} días</span>
      </div>

      {/* Contadores de HOY */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        {['opened', 'status_changed', 'comments', 'other'].map(k => {
          const m = KIND_META[k];
          return (
            <div key={k} className="bg-[var(--bg)] border border-[var(--border)] rounded-[10px] p-2.5 text-center" data-testid={`prod-today-${k}`}>
              <div className="text-xl">{m.icon}</div>
              <div className={`text-lg font-black ${m.color}`}>{today[k] || 0}</div>
              <div className="text-[9px] uppercase font-bold text-[var(--text-muted)] leading-tight">{m.label}</div>
            </div>
          );
        })}
      </div>

      {/* Sub-secciones */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* OTs asignadas activas */}
        <div>
          <p className="text-[10px] uppercase font-black text-[var(--text-muted)] mb-2">
            📋 OTs Activas ({ots.length})
          </p>
          {ots.length === 0 ? (
            <p className="text-xs text-[var(--text-muted)] italic py-2">Sin OTs asignadas activas.</p>
          ) : (
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {ots.slice(0, 10).map((ot) => (
                <a key={ot.id} href={`/produccion?ot=${ot.id}`}
                   className="block bg-[var(--bg)] border border-[var(--border)] rounded-md p-2 hover:border-[#6C3FB5]/50 transition-colors"
                   data-testid={`prod-ot-${ot.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono font-black text-xs text-[var(--text)]">{ot.numero}</span>
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${ESTADO_COLOR[ot.estado] || 'bg-gray-500/15 text-gray-400 border-gray-500/30'}`}>
                      {ESTADO_LABEL[ot.estado] || ot.estado}
                    </span>
                  </div>
                  <p className="text-xs text-[var(--text)] mt-0.5 truncate">{ot.cliente?.nombre || '—'}</p>
                  {ot.fecha_entrega_estimada && (
                    <p className="text-[10px] text-[var(--text-muted)]">Entrega: {ot.fecha_entrega_estimada}</p>
                  )}
                </a>
              ))}
              {ots.length > 10 && (
                <a href="/produccion" className="block text-center text-[11px] text-[#8B5CF6] hover:underline py-1">
                  Ver todas ({ots.length}) →
                </a>
              )}
            </div>
          )}
        </div>

        {/* Timeline reciente */}
        <div>
          <p className="text-[10px] uppercase font-black text-[var(--text-muted)] mb-2">
            ⏱️ Mis últimas acciones ({events.length})
          </p>
          {events.length === 0 ? (
            <p className="text-xs text-[var(--text-muted)] italic py-2">Sin actividad reciente. Cuando abras, actualices estado o comentes una OT aparecerá acá.</p>
          ) : (
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {events.slice(0, 15).map((ev, i) => {
                const meta = KIND_META[ev.kind] || KIND_META.other;
                return (
                  <a key={i} href={`/produccion?ot=${ev.ot_id}`}
                     className="block bg-[var(--bg)] border border-[var(--border)] rounded-md p-2 hover:border-[#6C3FB5]/50 transition-colors"
                     data-testid={`prod-event-${i}`}>
                    <div className="flex items-start gap-2">
                      <span className="text-sm shrink-0">{meta.icon}</span>
                      <div className="min-w-0 flex-1">
                        <p className={`text-[10px] font-black uppercase ${meta.color}`}>{ev.numero || '(sin OT)'} · {ev.cliente || '—'}</p>
                        <p className="text-xs text-[var(--text)] truncate">{ev.evento}</p>
                        <p className="text-[10px] text-[var(--text-muted)]">{new Date(ev.at).toLocaleString('es-GT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MiDia = () => {
  useOutletContext(); // ensure layout context exists
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [dashboard, setDashboard] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openType, setOpenType] = useState(null);
  const [editAct, setEditAct] = useState(null);
  const [weeklyData, setWeeklyData] = useState(null);
  const [myGoal, setMyGoal] = useState(null);
  const [prodActivity, setProdActivity] = useState(null);
  const [panel, setPanel] = useState(null);
  const [fabOpen, setFabOpen] = useState(false);
  const [showVentas, setShowVentas] = useState(false);
  const [showCumpl, setShowCumpl] = useState(false);
  const [seg, setSeg] = useState(null);
  const [nextActions, setNextActions] = useState(null);
  const [tabSup, setTabSup] = useState('mi');
  const navigate = useNavigate();

  const fetchAll = async () => {
    try {
      const now = new Date();
      const [d, a, w, g, p, s, n] = await Promise.all([
        api.get('/work-plan/dashboard/me'),
        api.get('/work-plan/activities/me?days=7'),
        api.get(`/work-plan/weekly-sales?user_id=${user.id}&year=${now.getFullYear()}&month=${now.getMonth() + 1}`),
        api.get(`/work-plan/goals?user_id=${user.id}&year=${now.getFullYear()}&month=${now.getMonth() + 1}`),
        api.get('/produccion/mi-actividad?days=7').catch(() => ({ data: null })),
        api.get('/cotizaciones/seguimiento?dias=3').catch(() => ({ data: null })),
        api.get('/work-plan/next-actions/me').catch(() => ({ data: null })),
      ]);
      setDashboard(d.data);
      setActivities(a.data || []);
      setWeeklyData(w.data);
      setMyGoal(Array.isArray(g.data) ? (g.data[0] || null) : g.data);
      setProdActivity(p.data);
      setSeg(s.data);
      setNextActions(n.data);
    } catch (e) {
      toast.error('Error cargando tu plan de trabajo');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) fetchAll();
  }, [isAdmin]);

  const deleteActivity = async (a) => {
    const label = (ACTIVITY_META[a.type]?.label || 'actividad').toLowerCase();
    if (!window.confirm(`¿Eliminar esta ${label} de "${a.customer_name}"? Quedará registro del cambio en la bitácora.`)) return;
    try {
      await api.delete(`/work-plan/activities/${a.id}`);
      toast.success('Actividad eliminada');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al eliminar');
    }
  };

  const marcarHecha = async (id) => {
    try {
      await api.post(`/work-plan/next-actions/${id}/done`);
      toast.success('Recordatorio completado ✅');
      fetchAll();
    } catch {
      toast.error('Error al marcar como hecha');
    }
  };

  // Admin sees executive dashboard (Centro de Control)
  if (isAdmin) {
    return <AdminDashboard />;
  }

  const esSupervisor = (user?.role || '').toLowerCase() === 'supervisor';

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[var(--bg)]">
        <CircleNotch size={32} className="animate-spin text-[#6C3FB5]" />
      </div>
    );
  }

  const today = dashboard?.today || {};
  const month = dashboard?.month || {};
  const goal = dashboard?.goal_progress;
  const activityGoals = dashboard?.activity_goals;

  const METAS = [
    { k: 'calls', type: 'call', icon: Phone, label: 'Llamadas', emoji: '📞', color: 'violet' },
    { k: 'visits', type: 'visit', icon: MapPin, label: 'Visitas', emoji: '📍', color: 'amber' },
    { k: 'quotes', type: 'quote', icon: FileText, label: 'Cotizaciones', emoji: '📄', color: 'purple' },
  ];
  const daily = activityGoals?.daily || {};
  const metasConTarget = METAS.filter((m) => daily[m.k]?.target > 0);
  const diaCumplido = metasConTarget.length > 0 && metasConTarget.every((m) => daily[m.k].percent >= 100);
  const motivacion = (() => {
    for (const m of metasConTarget) {
      const g = daily[m.k];
      const falta = g.target - g.done;
      if (falta > 0) return `Te ${falta === 1 ? 'falta' : 'faltan'} ${falta} ${falta === 1 ? m.label.toLowerCase().slice(0, -1) : m.label.toLowerCase()} para cumplir tu meta.`;
    }
    return null;
  })();

  const esHoy = (iso) => { try { return new Date(iso).toDateString() === new Date().toDateString(); } catch { return false; } };
  const actsHoy = (type) => activities.filter((a) => a.type === type && esHoy(a.happened_at));

  const segOrdenadas = [...(seg?.cotizaciones || [])].sort((a, b) =>
    (b.dias - a.dias) || ((b.total || 0) - (a.total || 0)));
  const proxima = segOrdenadas[0];

  const COLORES = {
    violet: { border: 'border-[#6C3FB5]/50', bg: 'bg-[#6C3FB5]/10', text: 'text-[#6C3FB5]', bar: 'bg-[#6C3FB5]' },
    amber: { border: 'border-[#6C3FB5]/40', bg: 'bg-[#6C3FB5]/5', text: 'text-[#6C3FB5]', bar: 'bg-[#6C3FB5]' },
    purple: { border: 'border-[#6C3FB5]/40', bg: 'bg-[#6C3FB5]/5', text: 'text-[#6C3FB5]', bar: 'bg-[#6C3FB5]' },
  };
  const PANEL_INFO = {
    call: { titulo: '📞 Llamadas de hoy', btn: '+ Registrar llamada' },
    visit: { titulo: '📍 Visitas de hoy', btn: '+ Registrar visita' },
    quote: { titulo: '📄 Cotizaciones de hoy', btn: '+ Registrar cotización' },
  };

  return (
    <div className="p-4 md:p-6 min-h-full pb-24" style={{ background: 'var(--bg)', color: 'var(--text)' }} data-testid="mi-dia-page">
      {esSupervisor && (
        <div className="flex gap-2 mb-4" data-testid="supervisor-tabs">
          <button onClick={() => setTabSup('mi')} data-testid="tab-mi-actividad"
            className={`flex-1 h-11 rounded-xl text-sm font-black border-2 transition-colors ${tabSup === 'mi' ? 'bg-[#6C3FB5] text-white border-[#6C3FB5]' : 'border-[var(--border)] text-[var(--text-muted)]'}`}>
            Mi actividad
          </button>
          <button onClick={() => setTabSup('equipo')} data-testid="tab-mi-equipo"
            className={`flex-1 h-11 rounded-xl text-sm font-black border-2 transition-colors ${tabSup === 'equipo' ? 'bg-[#6C3FB5] text-white border-[#6C3FB5]' : 'border-[var(--border)] text-[var(--text-muted)]'}`}>
            Mi equipo
          </button>
        </div>
      )}
      {esSupervisor && tabSup === 'equipo' ? (
        <EquipoSupervisor />
      ) : (
      <>
      {/* Header compacto */}
      <div className="mb-4">
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight heading-font">¡Hola, {user?.name?.split(' ')[0] || 'vendedor'}! 👋</h1>
        <p className="text-xs text-[var(--text-muted)] mt-0.5 flex items-center gap-1.5">
          <Calendar size={13} />
          {new Date().toLocaleDateString('es-GT', { weekday: 'long', day: 'numeric', month: 'long' })}
        </p>
        <p className="text-[11px] font-black uppercase tracking-[0.25em] text-[#6C3FB5] mt-2">🎯 Tu día</p>
      </div>

      <div className="lg:grid lg:grid-cols-3 lg:gap-5">
        {/* ===== COLUMNA PRINCIPAL ===== */}
        <div className="lg:col-span-2 space-y-4">
          {/* METAS DE HOY */}
          <div data-testid="daily-activity-goals">
            {diaCumplido && (
              <div className="mb-2 border-2 border-emerald-500/50 bg-emerald-500/10 rounded-[14px] p-3 text-center" data-testid="dia-cumplido">
                <p className="text-base font-black text-emerald-500">🎉 DÍA CUMPLIDO</p>
                <p className="text-[11px] text-[var(--text-muted)]">Completaste todas tus metas de hoy. ¡Excelente trabajo!</p>
              </div>
            )}
            <div className="grid grid-cols-3 gap-2">
              {METAS.map((m) => {
                const g = daily[m.k] || { done: today[m.k] || 0, target: 0, percent: 0 };
                const c = COLORES[m.color];
                const cumplida = g.target > 0 && g.percent >= 100;
                const activo = panel === m.type;
                return (
                  <button key={m.k} type="button" onClick={() => setPanel(activo ? null : m.type)}
                    className={`border-2 rounded-[14px] p-3 text-left transition-all active:scale-95 ${activo ? `${c.border} ${c.bg} ring-2 ring-offset-1 ring-[#6C3FB5]/30` : cumplida ? 'border-emerald-500/50 bg-emerald-500/10' : `${c.border} ${c.bg}`}`}
                    data-testid={`meta-card-${m.k}`}>
                    <p className="text-xl leading-none">{m.emoji}</p>
                    <p className="text-[10px] font-black uppercase tracking-wide text-[var(--text-muted)] mt-1">{m.label}</p>
                    <p className={`text-2xl font-black ${cumplida ? 'text-emerald-500' : 'text-[var(--text)]'}`}>
                      {cumplida && '✓ '}{g.done}<span className="text-sm text-[var(--text-faint)]"> / {g.target || '—'}</span>
                    </p>
                    {g.target > 0 ? (
                      <>
                        <div className="h-1.5 bg-[var(--surface-muted)] rounded-full overflow-hidden mt-1.5">
                          <div className={`h-full transition-all ${cumplida ? 'bg-emerald-500' : c.bar}`} style={{ width: `${Math.min(100, g.percent)}%` }} />
                        </div>
                        <p className={`text-[10px] font-black mt-1 ${cumplida ? 'text-emerald-500' : c.text}`}>
                          {cumplida ? 'META CUMPLIDA' : `${Math.round(g.percent)}%`}
                        </p>
                      </>
                    ) : (
                      <p className="text-[9px] text-[var(--text-faint)] mt-1">Sin meta configurada</p>
                    )}
                  </button>
                );
              })}
            </div>
            {!diaCumplido && motivacion && (
              <p className="text-[11px] font-bold text-amber-600 mt-2" data-testid="mensaje-motivacion">💪 {motivacion}</p>
            )}
          </div>

          {/* PANEL CONTEXTUAL de la meta tocada */}
          {panel && (
            <div className="border-2 border-[#6C3FB5]/40 bg-[var(--surface)] rounded-[14px] p-4" data-testid={`panel-${panel}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
                <p className="text-sm font-black uppercase tracking-wide">{PANEL_INFO[panel].titulo}</p>
                <div className="flex gap-2">
                  {panel === 'quote' && (
                    <Button variant="outline" className="h-10 text-xs font-bold" onClick={() => navigate('/cotizador')} data-testid="panel-abrir-cotizador">
                      Crear en Cotizador →
                    </Button>
                  )}
                  <Button className="h-10 bg-[#6C3FB5] hover:bg-[#4B1D73] text-white font-black" onClick={() => setOpenType(panel)} data-testid={`panel-registrar-${panel}`}>
                    {PANEL_INFO[panel].btn}
                  </Button>
                </div>
              </div>
              {actsHoy(panel).length === 0 ? (
                <p className="text-xs text-[var(--text-faint)]">Aún no registras {ACTIVITY_META[panel].label.toLowerCase()}s hoy. ¡Toca el botón para empezar!</p>
              ) : (
                <div className="space-y-1.5">
                  {actsHoy(panel).map((a) => (
                    <div key={a.id} className="flex items-center gap-2 border border-[var(--border)] rounded-[10px] px-3 py-2">
                      <CheckCircle size={16} weight="fill" className="text-emerald-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold truncate">{a.customer_name}</p>
                        <p className="text-[10px] text-[var(--text-muted)]">{a.outcome || ''} · {new Date(a.happened_at).toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                      {a.amount != null && <p className="text-xs font-black shrink-0">{formatMoney(a.amount)}</p>}
                      <button onClick={() => setEditAct(a)} className="p-1 text-[var(--text-muted)]" data-testid={`btn-edit-activity-${a.id}`}><PencilSimple size={13} weight="bold" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* RECORDATORIOS DE PRÓXIMA ACCIÓN */}
          {(nextActions?.acciones?.length > 0) && (
            <div className="border-2 border-amber-500/50 bg-amber-500/5 rounded-[14px] p-4" data-testid="recordatorios-hoy">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-600 mb-2">⏰ Próximas acciones prometidas ({nextActions.acciones.length})</p>
              <div className="space-y-2">
                {nextActions.acciones.map((na) => (
                  <div key={na.id} className="flex items-center gap-2 border border-[var(--border)] bg-[var(--surface)] rounded-[10px] px-3 py-2" data-testid={`recordatorio-${na.id}`}>
                    <span className="text-base shrink-0">{na.type === 'visit' ? '📍' : '📞'}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-black truncate">{na.customer_name}</p>
                      <p className={`text-[10px] font-bold ${na.vencida ? 'text-red-500' : 'text-amber-600'}`}>
                        {na.vencida ? `⚠ Vencida — era para el ${new Date(na.next_action_date + 'T12:00').toLocaleDateString('es-GT')}` : 'Prometida para HOY'}
                      </p>
                    </div>
                    {na.customer_phone && (
                      <a href={`tel:${na.customer_phone}`} className="h-9 px-2.5 inline-flex items-center rounded-lg bg-[#6C3FB5] text-white text-sm font-black" data-testid={`recordatorio-llamar-${na.id}`}>📞</a>
                    )}
                    <Button variant="outline" className="h-9 px-2.5 text-[11px] font-bold" onClick={() => setOpenType(na.type === 'visit' ? 'visit' : 'call')} data-testid={`recordatorio-registrar-${na.id}`}>Registrar</Button>
                    <button onClick={() => marcarHecha(na.id)} className="p-1.5 rounded-md bg-emerald-500/15 text-emerald-600 border border-emerald-500/30 hover:bg-emerald-500/25" title="Marcar hecha" data-testid={`recordatorio-hecha-${na.id}`}>
                      <CheckCircle size={15} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* PRÓXIMA ACCIÓN */}
          {proxima && (
            <div className="border-2 border-red-500/40 bg-red-500/5 rounded-[14px] p-4" data-testid="proxima-accion">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-red-500 mb-1.5">🔥 Próxima acción</p>
              <p className="text-sm font-black">📞 Llamar a {proxima.cliente}</p>
              <p className="text-[11px] text-[var(--text-muted)]">Seguimiento de cotización {proxima.numero} · {formatMoney(proxima.total)} · {proxima.dias} días sin movimiento</p>
              <div className="flex gap-2 mt-2.5">
                {proxima.telefono ? (
                  <a href={`tel:${proxima.telefono}`} className="h-11 flex-1 inline-flex items-center justify-center rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-black" data-testid="proxima-hacer-ahora">
                    📞 HACER AHORA
                  </a>
                ) : (
                  <Button className="h-11 flex-1 bg-red-600 hover:bg-red-700 text-white font-black" onClick={() => navigate(`/cotizador/${proxima.id}`)} data-testid="proxima-hacer-ahora">
                    HACER AHORA
                  </Button>
                )}
                <Button variant="outline" className="h-11" onClick={() => { setOpenType('call'); }} data-testid="proxima-registrar">Registrar resultado</Button>
              </div>
            </div>
          )}

          {/* Cotizaciones pendientes de seguimiento */}
          <SeguimientoCotizaciones />

          {/* Últimos 7 días */}
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-4">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle size={18} weight="bold" className="text-[var(--text-muted)]" />
              <h3 className="text-sm font-black heading-font uppercase tracking-wide">Últimos 7 días</h3>
              <span className="ml-auto text-[10px] text-[var(--text-faint)] font-bold">{activities.length} actividades</span>
            </div>
            {activities.length === 0 ? (
              <div className="text-center py-8 text-[var(--text-faint)]" data-testid="empty-activities">
                <Trophy size={36} weight="duotone" className="mx-auto mb-2 opacity-50" />
                <p className="text-sm font-bold">¡Empieza a registrar tu día!</p>
                <p className="text-xs">Usa el botón ＋ REGISTRAR para agregar llamadas, visitas, cotizaciones y ventas.</p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="activity-list">
                {activities.map((a) => {
                  const meta = ACTIVITY_META[a.type] || ACTIVITY_META.call;
                  const Icon = meta.icon;
                  return (
                    <div key={a.id} className={`flex items-center gap-3 p-3 rounded-[10px] border ${meta.border} ${meta.bg}`} data-testid={`activity-${a.id}`}>
                      <div className={`w-9 h-9 rounded-[8px] flex items-center justify-center shrink-0 ${meta.bg}`} style={{ color: meta.color }}>
                        <Icon size={18} weight="fill" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-bold truncate">{a.customer_name}</p>
                          {a.outcome && <span className={`text-[9px] uppercase tracking-wide font-black px-1.5 py-0.5 rounded ${meta.bg} ${meta.text}`}>{a.outcome}</span>}
                        </div>
                        <p className="text-[10px] text-[var(--text-muted)]">
                          {meta.label} · {new Date(a.happened_at).toLocaleString('es-GT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          {a.notes && <span className="text-[var(--text-faint)]"> · {a.notes.length > 50 ? a.notes.substring(0, 50) + '…' : a.notes}</span>}
                        </p>
                      </div>
                      {a.amount != null && <p className={`text-sm font-black ${meta.text} shrink-0`}>{formatMoney(a.amount)}</p>}
                      <div className="flex gap-0.5 shrink-0">
                        <button onClick={() => setEditAct(a)} className="p-1.5 rounded hover:bg-[var(--surface-muted)] text-[var(--text-muted)]" title="Editar" data-testid={`btn-edit-activity-${a.id}`}>
                          <PencilSimple size={14} weight="bold" />
                        </button>
                        <button onClick={() => deleteActivity(a)} className="p-1.5 rounded hover:bg-red-500/10 text-red-500" title="Eliminar" data-testid={`btn-delete-activity-${a.id}`}>
                          <Trash size={14} weight="bold" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ===== COLUMNA SECUNDARIA ===== */}
        <div className="space-y-4 mt-4 lg:mt-0">
          {/* HOY — resumen rápido */}
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-4" data-testid="resumen-hoy">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text-muted)] mb-2">Hoy</p>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs font-bold">
              <p>📞 {today.calls || 0} llamada{(today.calls || 0) !== 1 ? 's' : ''}</p>
              <p>📍 {today.visits || 0} visita{(today.visits || 0) !== 1 ? 's' : ''}</p>
              <p>📄 {today.quotes || 0} cotización{(today.quotes || 0) !== 1 ? 'es' : ''}</p>
              <p>💰 {formatMoney(today.sales_amount)} vendido</p>
            </div>
          </div>

          {/* META DEL MES */}
          {goal && (
            <div className="bg-gradient-to-br from-emerald-500/10 to-blue-500/10 border border-emerald-500/30 rounded-[14px] p-4" data-testid="goal-progress-card">
              <div className="flex items-center gap-2 mb-1">
                <Target size={18} weight="fill" className="text-emerald-400" />
                <p className="text-[10px] uppercase tracking-wide text-emerald-400 font-black">🎯 Meta del mes</p>
                <span className="ml-auto text-lg font-black text-emerald-400">{goal.percent}%</span>
              </div>
              <p className="text-sm font-black">{formatMoney(goal.achieved_amount)} / {formatMoney(goal.target_amount)}</p>
              <div className="h-2 bg-[var(--surface-muted)] rounded-full overflow-hidden mt-1.5">
                <div className="h-full bg-gradient-to-r from-emerald-500 to-blue-500 transition-all" style={{ width: `${Math.min(100, goal.percent)}%` }} />
              </div>
              {goal.target_amount > goal.achieved_amount && (
                <p className="text-[11px] font-bold text-[var(--text-muted)] mt-1.5" data-testid="meta-falta">Te faltan {formatMoney(goal.target_amount - goal.achieved_amount)}</p>
              )}
              {goal.percent >= 80 && goal.percent < 100 && <p className="text-[11px] font-black text-emerald-500 mt-0.5">🔥 Vas en {goal.percent}% de tu meta.</p>}
              {goal.commission_rate_at_target && (
                <p className="text-[10px] text-[var(--text-muted)] mt-1">Comisión al alcanzar meta: <strong className="text-emerald-400">{(goal.commission_rate_at_target * 100).toFixed(1)}%</strong></p>
              )}
            </div>
          )}

          {/* MIS VENTAS CRM vs HarpERP — colapsada por defecto */}
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-4">
            <button type="button" className="w-full flex items-center justify-between" onClick={() => setShowVentas(!showVentas)} data-testid="toggle-ventas">
              <span className="text-[11px] font-black uppercase tracking-wide">📊 Mis ventas · CRM vs HarpERP</span>
              <span className="text-xs font-bold text-[#6C3FB5]">{showVentas ? 'Ocultar' : 'Ver comparación'}</span>
            </button>
            {showVentas && <div className="mt-3"><MisVentas /></div>}
          </div>

          {/* CUMPLIMIENTO CYH + SA — colapsada por defecto */}
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-4">
            <button type="button" className="w-full flex items-center justify-between" onClick={() => setShowCumpl(!showCumpl)} data-testid="toggle-cumplimiento">
              <span className="text-[11px] font-black uppercase tracking-wide">🏆 Mi cumplimiento · CYH + SA</span>
              <span className="text-xs font-bold text-[#6C3FB5]">{showCumpl ? 'Ocultar' : 'Ver detalle'}</span>
            </button>
            {showCumpl && <div className="mt-3"><WeeklyProgressCard myGoal={myGoal} weeklyData={weeklyData} /></div>}
          </div>

          {/* Producción */}
          {prodActivity && (prodActivity.assigned_count > 0 || prodActivity.today?.total > 0 || (prodActivity.my_events || []).length > 0) && (
            <ProduccionActivityCard data={prodActivity} />
          )}

          {/* Mi Mes */}
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-4">
            <div className="flex items-center gap-2 mb-3">
              <ChartLineUp size={18} weight="bold" className="text-[#8B5CF6]" />
              <h3 className="text-sm font-black heading-font uppercase tracking-wide">Mi Mes</h3>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <MetricChip label="Llamadas" value={month.calls || 0} icon={Phone} color="blue" />
              <MetricChip label="Visitas" value={month.visits || 0} icon={MapPin} color="amber" />
              <MetricChip label="Cotizaciones" value={month.quotes || 0} icon={FileText} color="purple" sub={formatMoney(month.quotes_amount)} />
              <MetricChip label="Ventas" value={month.sales || 0} icon={CurrencyDollar} color="emerald" sub={formatMoney(month.sales_amount)} highlight />
            </div>
          </div>
        </div>
      </div>

      {/* FAB ＋ REGISTRAR */}
      <div className="fixed bottom-5 right-20 z-40">
        {fabOpen && (
          <div className="absolute bottom-16 right-0 w-56 bg-[var(--surface)] border-2 border-[var(--border)] rounded-2xl shadow-2xl p-2 space-y-1.5" data-testid="fab-opciones">
            {Object.entries(ACTIVITY_META).map(([key, meta]) => {
              const Icon = meta.icon;
              return (
                <button key={key} onClick={() => { setFabOpen(false); setOpenType(key); }}
                  className={`w-full flex items-center gap-3 ${meta.bg} ${meta.border} ${meta.text} border rounded-xl p-3 text-left active:scale-95 transition-transform`}
                  data-testid={`btn-add-${key}`}>
                  <Icon size={22} weight="fill" />
                  <span className="text-sm font-black">{meta.label}</span>
                </button>
              );
            })}
          </div>
        )}
        <button onClick={() => setFabOpen(!fabOpen)}
          className={`h-14 px-5 rounded-full shadow-xl font-black text-white text-sm flex items-center gap-2 transition-transform active:scale-95 ${fabOpen ? 'bg-slate-600' : 'bg-[#6C3FB5] hover:bg-[#4B1D73]'}`}
          data-testid="fab-registrar">
          {fabOpen ? '✕ Cerrar' : '＋ REGISTRAR'}
        </button>
      </div>

      {/* Modal: Add activity */}
      <ActivityModal
        type={openType}
        onClose={() => setOpenType(null)}
        onSaved={() => { setOpenType(null); fetchAll(); }}
      />
      {editAct && (
        <EditActivityModal
          activity={editAct}
          onClose={() => setEditAct(null)}
          onSaved={() => { setEditAct(null); fetchAll(); }}
        />
      )}
      </>
      )}
    </div>
  );
};

const EquipoSupervisor = () => {
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  useEffect(() => {
    const now = new Date();
    api.get(`/reportes/kpi-mensual?year=${now.getFullYear()}&month=${now.getMonth() + 1}`, { timeout: 120000 })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setCargando(false));
  }, []);
  if (cargando) return <div className="flex justify-center py-16"><CircleNotch size={28} className="animate-spin text-[#6C3FB5]" /></div>;
  if (!data) return <p className="text-sm text-[var(--text-muted)] text-center py-10">No tienes acceso al resumen del equipo o no hay datos.</p>;
  const vendors = (data.vendors || []).filter((v) => (v.role || 'user') === 'user' || v.has_goals);
  const sum = (k) => vendors.reduce((a, v) => a + (Number(v[k]) || 0), 0);
  const atencion = vendors.filter((v) => (v.calls + v.visits + v.quotes + v.sales) === 0 || (v.overall_pct !== null && v.overall_pct < 40));
  const Q = (n) => `Q${Number(n || 0).toLocaleString()}`;
  const medallas = ['🥇', '🥈', '🥉'];
  const rank = (key, money) => [...vendors].sort((a, b) => (b[key] || 0) - (a[key] || 0)).slice(0, 5)
    .map((v, i) => (
      <div key={v.user_id || i} className="flex justify-between text-xs py-1 border-b border-[var(--border)] last:border-0">
        <span className="font-bold text-[var(--text)]">{medallas[i] || `${i + 1}.`} {v.name}</span>
        <span className="font-black text-[var(--text)]">{money ? Q(v[key]) : (v[key] || 0)}</span>
      </div>
    ));
  return (
    <div className="space-y-4" data-testid="equipo-supervisor">
      <p className="text-[11px] font-black uppercase tracking-[0.25em] text-[#6C3FB5]">👥 Mi equipo · {data.month_label}</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[['📞 Llamadas', sum('calls')], ['📍 Visitas', sum('visits')], ['📄 Cotizaciones', sum('quotes')], ['💰 Ventas CRM', Q(sum('sales_amount'))]].map(([l, v]) => (
          <div key={l} className="border border-[#6C3FB5]/30 bg-[#6C3FB5]/5 rounded-[12px] p-3">
            <p className="text-[10px] font-black text-[var(--text-muted)]">{l}</p>
            <p className="text-xl font-black text-[var(--text)]">{v}</p>
          </div>
        ))}
      </div>
      {atencion.length > 0 && (
        <div className="border-2 border-[#E99A22]/50 bg-[#E99A22]/10 rounded-[14px] p-3" data-testid="equipo-atencion">
          <p className="text-xs font-black text-[#E99A22] uppercase mb-1.5">⚠ Necesitan tu apoyo</p>
          {atencion.slice(0, 5).map((v) => (
            <p key={v.user_id} className="text-xs text-[var(--text)] py-0.5">
              <b>{v.name}</b> — {(v.calls + v.visits + v.quotes + v.sales) === 0 ? 'sin actividad este mes' : `cumplimiento ${Math.round(v.overall_pct)}%`}
            </p>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="border border-[var(--border)] rounded-[14px] p-3"><p className="text-[10px] font-black uppercase text-[var(--text-muted)] mb-1">💰 Monto vendido CRM</p>{rank('sales_amount', true)}</div>
        <div className="border border-[var(--border)] rounded-[14px] p-3"><p className="text-[10px] font-black uppercase text-[var(--text-muted)] mb-1">📄 Monto cotizado</p>{rank('quotes_amount', true)}</div>
        <div className="border border-[var(--border)] rounded-[14px] p-3"><p className="text-[10px] font-black uppercase text-[var(--text-muted)] mb-1">🏢 Facturación HarpERP</p>{rank('erp_total', true)}</div>
        <div className="border border-[var(--border)] rounded-[14px] p-3"><p className="text-[10px] font-black uppercase text-[var(--text-muted)] mb-1">📞 Actividad (llamadas)</p>{rank('calls', false)}</div>
      </div>
      <div className="space-y-2" data-testid="equipo-vendedores">
        {vendors.map((v) => (
          <div key={v.user_id} className="border border-[var(--border)] rounded-[12px] p-3 bg-[var(--surface)]">
            <div className="flex items-center justify-between">
              <p className="text-sm font-black text-[var(--text)]">{v.name} <span className="text-[10px] font-bold text-[var(--text-muted)]">{v.branch || ''}</span></p>
              <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${v.overall_pct === null ? 'bg-[var(--surface-muted)] text-[var(--text-muted)]' : v.overall_pct >= 100 ? 'bg-[#22A66F]/15 text-[#22A66F]' : v.overall_pct >= 40 ? 'bg-[#E99A22]/15 text-[#E99A22]' : 'bg-[#D92D20]/15 text-[#D92D20]'}`}>
                {v.overall_pct === null ? 'sin meta' : `${Math.round(v.overall_pct)}% meta`}
              </span>
            </div>
            <p className="text-[11px] text-[var(--text-muted)] mt-1">📞 {v.calls} · 📍 {v.visits} · 📄 {v.quotes} ({Q(v.quotes_amount)}) · 💰 {v.sales} ({Q(v.sales_amount)})</p>
          </div>
        ))}
      </div>
    </div>
  );
};
const MetricChip = ({ label, value, icon: Icon, color, sub, highlight }) => {
  const colors = {
    blue: 'text-[#8B5CF6] border-blue-500/20 bg-[#6C3FB5]/5',
    amber: 'text-amber-400 border-amber-500/20 bg-amber-500/5',
    purple: 'text-purple-400 border-purple-500/20 bg-purple-500/5',
    emerald: 'text-emerald-400 border-emerald-500/20 bg-emerald-500/5',
  };
  return (
    <div className={`border rounded-[12px] p-4 ${colors[color]} ${highlight ? 'ring-2 ring-emerald-500/30' : ''}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={14} weight="bold" />
        <span className="text-[10px] uppercase tracking-wide font-bold opacity-80">{label}</span>
      </div>
      <p className="text-2xl font-black">{value}</p>
      {sub && <p className="text-[10px] font-bold opacity-80">{sub}</p>}
    </div>
  );
};

const PRODUCT_LIST = [
  { key: 'herrajes', label: 'Herrajes' },
  { key: 'pasamanos', label: 'Pasamanos' },
  { key: 'vidrio_templado', label: 'Vidrio templado' },
  { key: 'pvc', label: 'PVC' },
  { key: 'aluminio', label: 'Aluminio' },
  { key: 'techo', label: 'Techo' },
  { key: 'techo_curvo', label: 'Techo curvo' },
  { key: 'tablayeso', label: 'Tablayeso' },
  { key: 'puertas_automaticas', label: 'Puertas automáticas' },
  { key: 'reparacion', label: 'Reparación' },
  { key: 'peliculas', label: 'Películas' },
  { key: 'herraje_especial', label: 'Herraje especial' },
  { key: 'estructura_metalica', label: 'Estructura metálica' },
  { key: 'puertas_metal', label: 'Puertas metal' },
  { key: 'otros', label: 'Otros' },
];

const CLIENT_TYPE_LIST = [
  { key: 'proyecto', label: 'Proyecto' },
  { key: 'vidrieria', label: 'Vidriería' },
  { key: 'instalador', label: 'Instalador' },
  { key: 'constructora', label: 'Constructora' },
  { key: 'cliente_final', label: 'Cliente final' },
  { key: 'otro', label: 'Otro' },
];

const GT_DEPARTAMENTOS = [
  "Guatemala", "Sacatepéquez", "Chimaltenango", "El Progreso", "Escuintla",
  "Santa Rosa", "Sololá", "Totonicapán", "Quetzaltenango", "Suchitepéquez",
  "Retalhuleu", "San Marcos", "Huehuetenango", "Quiché", "Baja Verapaz",
  "Alta Verapaz", "Petén", "Izabal", "Zacapa", "Chiquimula", "Jalapa", "Jutiapa",
];

const EMPTY_FORM = {
  customer_name: '',
  customer_phone: '',
  amount: '',
  outcome: '',
  notes: '',
  business_tag: '',
  // quote
  quote_number: '',
  products: [],
  quote_file_url: '',
  quote_file_name: '',
  // visit
  company_name: '',
  contact_person: '',
  address: '',
  visit_description: '',
  client_type: '',
  // geographic (quote + visit)
  departamento: '',
};

const MOTIVOS_LLAMADA = ['Prospección', 'Seguimiento', 'Cotización', 'Cobro', 'Posventa', 'Otro'];
const TIPOS_VISITA = ['Prospección', 'Presentación', 'Medición', 'Seguimiento', 'Cierre', 'Posventa', 'Otro'];
const PROX_ACCION = ['Hoy', 'Mañana', 'Elegir fecha', 'Ninguna'];

const QuickProxAccion = ({ proxAccion, setProxAccion, proxFecha, setProxFecha }) => (
  <div>
    <Label>Próxima acción</Label>
    <div className="flex flex-wrap gap-1.5 mt-1">
      {PROX_ACCION.map((p) => (
        <button type="button" key={p} onClick={() => setProxAccion(proxAccion === p ? '' : p)}
          className={`text-[11px] font-bold px-2.5 py-1.5 rounded-md border transition-colors ${proxAccion === p ? 'bg-[#6C3FB5]/15 text-[#6C3FB5] border-[#6C3FB5]/50' : 'bg-white/5 text-[var(--text-muted)] border-[var(--border)]'}`}
          data-testid={`prox-${p}`}>{p}</button>
      ))}
    </div>
    {proxAccion === 'Elegir fecha' && (
      <Input type="date" value={proxFecha} onChange={(e) => setProxFecha(e.target.value)} className="mt-2 bg-[var(--bg)] border-[var(--border)]" data-testid="prox-fecha" />
    )}
  </div>
);

const ActivityModal = ({ type, onClose, onSaved }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [location, setLocation] = useState(null); // {lat, lng, accuracy}
  const [locating, setLocating] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [proxAccion, setProxAccion] = useState('');
  const [proxFecha, setProxFecha] = useState('');
  const [clienteSel, setClienteSel] = useState(null);

  // Borrador local seguro: respaldo temporal (24h) ante cierres inesperados; se borra al guardar
  const draftKey = type ? `midia_draft_${type}` : null;
  useEffect(() => {
    if (!type) return;
    setForm(EMPTY_FORM);
    setLocation(null);
    setMotivo('');
    setProxAccion('');
    setProxFecha('');
    setClienteSel(null);
    try {
      const raw = localStorage.getItem(`midia_draft_${type}`);
      if (raw) {
        const d = JSON.parse(raw);
        if (Date.now() - d.ts < 24 * 60 * 60 * 1000 && d.form) {
          setForm({ ...EMPTY_FORM, ...d.form });
          setMotivo(d.motivo || '');
          setProxAccion(d.proxAccion || '');
          setProxFecha(d.proxFecha || '');
          toast.info('Recuperamos tu borrador sin guardar 📝', { duration: 4000 });
        } else localStorage.removeItem(`midia_draft_${type}`);
      }
    } catch { /* sin borrador */ }
  }, [type]);

  useEffect(() => {
    if (!draftKey) return;
    const hayDatos = Object.values(form).some((v) => v && String(v).trim());
    const t = setTimeout(() => {
      try {
        if (hayDatos) localStorage.setItem(draftKey, JSON.stringify({ ts: Date.now(), form, motivo, proxAccion, proxFecha }));
      } catch { /* almacenamiento lleno */ }
    }, 600);
    return () => clearTimeout(t);
  }, [form, motivo, proxAccion, proxFecha, draftKey]);

  const captureLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Tu navegador no soporta GPS');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
        setLocating(false);
        toast.success(`Ubicación capturada (±${Math.round(pos.coords.accuracy)}m)`);
      },
      (err) => {
        setLocating(false);
        toast.error(err.code === 1 ? 'Permiso denegado' : 'No se pudo obtener ubicación');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const toggleProduct = (key) => {
    setForm((f) => {
      const products = f.products.includes(key)
        ? f.products.filter((p) => p !== key)
        : [...f.products, key];
      return { ...f, products };
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Archivo demasiado grande (máx 10 MB)');
      return;
    }
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/work-plan/activities/upload-quote', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm((f) => ({ ...f, quote_file_url: r.data.url, quote_file_name: r.data.filename }));
      toast.success('Archivo adjuntado ✅');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error subiendo archivo');
    } finally {
      setUploadingFile(false);
    }
  };

  if (!type) return null;
  const meta = ACTIVITY_META[type];

  const submit = async (e) => {
    e?.preventDefault?.();
    // ── Client-side validation by type ──
    if (type === 'call') {
      if (!form.customer_name.trim()) return toast.error('Nombre del cliente requerido');
      if (!form.customer_phone.trim()) return toast.error('Teléfono requerido');
      if (!form.outcome) return toast.error('Selecciona el resultado de la llamada');
      if (!form.notes.trim() && !motivo) return toast.error('Selecciona el motivo o escribe una nota');
    }
    if (type === 'visit') {
      if (!form.company_name.trim()) return toast.error('Cliente / Empresa requerido');
      if (!form.customer_phone.trim()) return toast.error('Teléfono requerido');
      if (!form.contact_person.trim()) return toast.error('Persona de contacto requerida');
      if (!form.address.trim()) return toast.error('Dirección requerida');
      if (!form.visit_description.trim()) return toast.error('Descripción de la visita requerida');
      if (!form.client_type) return toast.error('Selecciona el tipo de cliente');
    }
    if (type === 'quote') {
      if (!form.customer_name.trim()) return toast.error('Nombre del cliente requerido');
      if (!form.amount || isNaN(parseFloat(form.amount)) || parseFloat(form.amount) <= 0) {
        return toast.error('Monto Q requerido (> 0)');
      }
      if (form.products.length === 0) return toast.error('Selecciona al menos 1 producto');
      if (!form.departamento) return toast.error('Selecciona el departamento');
    }
    if (type === 'sale') {
      if (!form.customer_name.trim()) return toast.error('Nombre del cliente requerido');
      if (!form.amount || isNaN(parseFloat(form.amount))) return toast.error('Monto requerido');
      if (!form.business_tag) return toast.error('Selecciona empresa (CYH/SA)');
    }

    setSaving(true);
    try {
      const extras = [];
      if (motivo) extras.push(`${type === 'visit' ? 'Tipo' : 'Motivo'}: ${motivo}`);
      if (proxAccion && proxAccion !== 'Ninguna') {
        const f = proxAccion === 'Hoy' ? new Date().toLocaleDateString('es-GT')
          : proxAccion === 'Mañana' ? new Date(Date.now() + 86400000).toLocaleDateString('es-GT')
          : (proxFecha ? new Date(proxFecha + 'T12:00').toLocaleDateString('es-GT') : '');
        if (f) extras.push(`Próxima acción: ${f}`);
      }
      const notasFinal = [...extras, form.notes.trim()].filter(Boolean).join(' · ');
      const localISO = (d) => { const x = new Date(d); x.setMinutes(x.getMinutes() - x.getTimezoneOffset()); return x.toISOString().slice(0, 10); };
      let nextActionDate = null;
      if (proxAccion === 'Hoy') nextActionDate = localISO(new Date());
      else if (proxAccion === 'Mañana') nextActionDate = localISO(new Date(Date.now() + 86400000));
      else if (proxAccion === 'Elegir fecha' && proxFecha) nextActionDate = proxFecha;
      const payload = {
        type,
        customer_name: (form.company_name || form.customer_name).trim(),
        customer_phone: form.customer_phone.trim() || null,
        amount: (type === 'quote' || type === 'sale') ? parseFloat(form.amount) : null,
        outcome: form.outcome || null,
        notes: notasFinal || null,
        business_tag: form.business_tag || null,
        cliente_id: clienteSel?.id || null,
        next_action_date: nextActionDate,
        location_lat: location?.lat ?? null,
        location_lng: location?.lng ?? null,
        location_accuracy: location?.accuracy ?? null,
      };
      if (type === 'quote') {
        payload.quote_number = form.quote_number.trim();
        payload.products = form.products;
        payload.quote_file_url = form.quote_file_url || null;
        payload.address = form.address.trim() || null;
        payload.departamento = form.departamento || null;
      }
      if (type === 'visit') {
        payload.company_name = form.company_name.trim();
        payload.contact_person = form.contact_person.trim();
        payload.address = form.address.trim();
        payload.visit_description = form.visit_description.trim();
        payload.client_type = form.client_type;
        payload.departamento = form.departamento || null;
      }
      await api.post('/work-plan/activities', payload);
      toast.success(`${meta.label} registrada ✅`);
      try { localStorage.removeItem(`midia_draft_${type}`); } catch { /* ok */ }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error guardando');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!type} onOpenChange={onClose}>
      <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] max-w-md max-h-[92vh] flex flex-col p-0 gap-0" data-testid={`activity-modal-${type}`}>
        <DialogHeader className="px-5 pt-5 pb-3 shrink-0 border-b border-[var(--border)]">
          <DialogTitle className="flex items-center gap-2 text-[var(--text)]">
            <meta.icon size={22} weight="fill" style={{ color: meta.color }} />
            Registrar {meta.label}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 flex-1 overflow-y-auto px-5 py-4 min-h-0">

          {/* ─── CALL ─── */}
          {type === 'call' && (
            <>
              <ClienteBuscador label="Cliente *" value={form.customer_name} autoFocus
                placeholder="Busca un cliente o escribe el nombre"
                telefonoActual={form.customer_phone}
                onChangeText={(v) => { setClienteSel(null); setForm((f) => ({ ...f, customer_name: v })); }}
                onSelect={(c) => { setClienteSel(c); setForm((f) => ({ ...f, customer_name: c.nombre, customer_phone: c.telefono || c.whatsapp || f.customer_phone })); }}
                testid="modal-customer-name" />
              <HistorialCliente nombre={clienteSel?.nombre} telefono={clienteSel?.telefono || clienteSel?.whatsapp} clienteId={clienteSel?.id} />
              <div>
                <Label>Teléfono *</Label>
                <Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} placeholder="+502 ..." className="bg-[var(--bg)] border-[var(--border)]" data-testid="modal-customer-phone" />
              </div>
              <div>
                <Label>Resultado *</Label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {OUTCOMES[type].map((o) => (
                    <button type="button" key={o}
                      onClick={() => setForm({ ...form, outcome: o })}
                      className={`text-[11px] font-bold px-2.5 py-1 rounded-md border transition-colors ${form.outcome === o ? `${meta.bg} ${meta.text} ${meta.border}` : 'bg-white/5 text-[var(--text-muted)] border-[var(--border)] hover:bg-white/10'}`}
                      data-testid={`outcome-${o}`}>{o}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Motivo de la llamada</Label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {MOTIVOS_LLAMADA.map((m) => (
                    <button type="button" key={m} onClick={() => setMotivo(motivo === m ? '' : m)}
                      className={`text-[11px] font-bold px-2.5 py-1.5 rounded-md border transition-colors ${motivo === m ? 'bg-[#6C3FB5]/15 text-[#6C3FB5] border-[#6C3FB5]/50' : 'bg-white/5 text-[var(--text-muted)] border-[var(--border)]'}`}
                      data-testid={`motivo-${m}`}>{m}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Notas / Detalle{motivo ? ' (opcional)' : ' *'}</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} placeholder="¿De qué hablaron? Próximo paso..." className="bg-[var(--bg)] border-[var(--border)]" data-testid="modal-notes" />
              </div>
              <QuickProxAccion proxAccion={proxAccion} setProxAccion={setProxAccion} proxFecha={proxFecha} setProxFecha={setProxFecha} />
            </>
          )}

          {/* ─── VISIT ─── */}
          {type === 'visit' && (
            <>
              <ClienteBuscador label="Cliente / Empresa *" value={form.company_name} autoFocus
                placeholder="Busca un cliente o escribe el nombre"
                telefonoActual={form.customer_phone}
                onChangeText={(v) => { setClienteSel(null); setForm((f) => ({ ...f, company_name: v })); }}
                onSelect={(c) => { setClienteSel(c); setForm((f) => ({
                  ...f,
                  company_name: c.nombre,
                  customer_phone: c.telefono || c.whatsapp || f.customer_phone,
                  address: f.address || c.direccion || '',
                  contact_person: f.contact_person || c.contacto || '',
                })); }}
                testid="modal-company-name" />
              <HistorialCliente nombre={clienteSel?.nombre} telefono={clienteSel?.telefono || clienteSel?.whatsapp} clienteId={clienteSel?.id} />
              <div>
                <Label>Teléfono *</Label>
                <Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} placeholder="+502 ..." className="bg-[var(--bg)] border-[var(--border)]" data-testid="modal-customer-phone" />
              </div>
              <div>
                <Label>Persona de Contacto *</Label>
                <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} placeholder="Nombre del responsable" className="bg-[var(--bg)] border-[var(--border)]" data-testid="modal-contact-person" />
              </div>
              <div>
                <Label>Dirección *</Label>
                <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} placeholder="Calle, zona, depto..." className="bg-[var(--bg)] border-[var(--border)]" data-testid="modal-address" />
              </div>
              <div>
                <Label>Tipo de Cliente *</Label>
                <div className="grid grid-cols-2 gap-1.5 mt-1">
                  {CLIENT_TYPE_LIST.map((c) => (
                    <button type="button" key={c.key}
                      onClick={() => setForm({ ...form, client_type: c.key })}
                      className={`text-[11px] font-bold px-2.5 py-2 rounded-md border transition-colors ${form.client_type === c.key ? 'bg-[#1D4ED8] text-white border-[#1D4ED8]' : 'bg-[var(--surface)] text-[var(--text-muted)] border-[var(--border)] hover:bg-[var(--bg)]'}`}
                      data-testid={`client-type-${c.key}`}>{c.label}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Tipo de visita</Label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {TIPOS_VISITA.map((m) => (
                    <button type="button" key={m} onClick={() => setMotivo(motivo === m ? '' : m)}
                      className={`text-[11px] font-bold px-2.5 py-1.5 rounded-md border transition-colors ${motivo === m ? 'bg-[#6C3FB5]/15 text-[#6C3FB5] border-[#6C3FB5]/50' : 'bg-white/5 text-[var(--text-muted)] border-[var(--border)]'}`}
                      data-testid={`tipo-visita-${m}`}>{m}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Descripción de la Visita *</Label>
                <Textarea value={form.visit_description} onChange={(e) => setForm({ ...form, visit_description: e.target.value })} rows={3} placeholder="Motivo, qué se trabajó, próximos pasos..." className="bg-[var(--bg)] border-[var(--border)]" data-testid="modal-visit-description" />
              </div>
              <QuickProxAccion proxAccion={proxAccion} setProxAccion={setProxAccion} proxFecha={proxFecha} setProxFecha={setProxFecha} />
              <div>
                <Label>Departamento <span className="text-[10px] text-[var(--text-muted)]">(para sectorizar)</span></Label>
                <select value={form.departamento} onChange={(e) => setForm({ ...form, departamento: e.target.value })}
                  className="w-full px-3 py-2 text-sm rounded-md bg-[var(--bg)] border border-[var(--border)] text-[var(--text)]"
                  data-testid="modal-departamento">
                  <option value="">(Sin especificar)</option>
                  {GT_DEPARTAMENTOS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div className="bg-[var(--surface-muted)] border border-[var(--border)] rounded-md p-2.5">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <MapPin size={16} weight="bold" className="text-amber-500 shrink-0" />
                    <div className="text-[11px] min-w-0">
                      <p className="font-bold text-[var(--text)]">Ubicación GPS (check-in)</p>
                      {location ? (
                        <p className="text-[10px] text-[var(--text-muted)] truncate" data-testid="gps-location">
                          {location.lat.toFixed(5)}, {location.lng.toFixed(5)} · ±{Math.round(location.accuracy)}m
                        </p>
                      ) : (
                        <p className="text-[10px] text-[var(--text-faint)]">Opcional - presiona Check-in</p>
                      )}
                    </div>
                  </div>
                  <button type="button" onClick={captureLocation} disabled={locating}
                    className="text-[10px] font-bold px-2 py-1.5 rounded bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-500/30 hover:bg-amber-500/30 disabled:opacity-50 shrink-0"
                    data-testid="btn-capture-gps">
                    {locating ? <CircleNotch size={12} className="animate-spin inline" /> : (location ? 'Re-capturar' : '📍 Check-in')}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* ─── QUOTE ─── */}
          {type === 'quote' && (
            <>
              <div>
                <Label>Cliente *</Label>
                <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Nombre del cliente" className="bg-[var(--bg)] border-[var(--border)]" autoFocus data-testid="modal-customer-name" />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} placeholder="+502 ..." className="bg-[var(--bg)] border-[var(--border)]" data-testid="modal-customer-phone" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>N° Cotización <span className="text-[10px] text-[var(--text-muted)]">(opcional)</span></Label>
                  <Input value={form.quote_number} onChange={(e) => setForm({ ...form, quote_number: e.target.value })} placeholder="COT-001" className="bg-[var(--bg)] border-[var(--border)]" data-testid="modal-quote-number" />
                </div>
                <div>
                  <Label>Monto Total Q *</Label>
                  <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" className="bg-[var(--bg)] border-[var(--border)]" data-testid="modal-amount" />
                </div>
              </div>
              <div>
                <Label>Productos cotizados * <span className="text-[10px] text-[var(--text-muted)]">(selecciona uno o varios)</span></Label>
                <div className="grid grid-cols-2 gap-1 mt-1 max-h-[180px] overflow-y-auto p-1.5 bg-[var(--bg)] rounded-md border border-[var(--border)]">
                  {PRODUCT_LIST.map((p) => (
                    <label key={p.key} className={`flex items-center gap-1.5 p-1.5 rounded cursor-pointer border text-[11px] transition-all ${form.products.includes(p.key) ? 'border-[#1D4ED8] bg-[#1D4ED8]/10' : 'border-transparent hover:bg-[var(--surface)]'}`}>
                      <input type="checkbox" checked={form.products.includes(p.key)} onChange={() => toggleProduct(p.key)} className="w-3.5 h-3.5 accent-[#1D4ED8]" data-testid={`product-${p.key}`} />
                      <span className="font-bold text-[var(--text)]">{p.label}</span>
                    </label>
                  ))}
                </div>
                {form.products.length > 0 && (
                  <p className="text-[10px] text-[var(--text-muted)] mt-1">
                    {form.products.length} producto(s) seleccionado(s)
                  </p>
                )}
              </div>
              <div>
                <Label>Adjuntar cotización <span className="text-[10px] text-[var(--text-muted)]">(opcional - PDF/JPG/PNG, máx 10 MB)</span></Label>
                {form.quote_file_url ? (
                  <div className="flex items-center justify-between p-2 bg-emerald-500/10 border border-emerald-500/30 rounded-md mt-1">
                    <span className="text-xs font-bold text-emerald-700 dark:text-emerald-300 truncate">📎 {form.quote_file_name || 'Archivo adjunto'}</span>
                    <button type="button" onClick={() => setForm({ ...form, quote_file_url: '', quote_file_name: '' })} className="text-xs text-red-500 hover:underline">Quitar</button>
                  </div>
                ) : (
                  <Input type="file" accept=".pdf,.jpg,.jpeg,.png,image/*,application/pdf" onChange={handleFileUpload} disabled={uploadingFile} className="bg-[var(--bg)] border-[var(--border)] text-xs" data-testid="modal-quote-file" />
                )}
                {uploadingFile && <p className="text-[10px] text-amber-500 mt-1"><CircleNotch size={10} className="animate-spin inline mr-1" />Subiendo...</p>}
              </div>
              <div>
                <Label>Dirección de la obra/cotización <span className="text-[10px] text-[var(--text-muted)]">(opcional)</span></Label>
                <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} placeholder="Calle, zona, referencia..." className="bg-[var(--bg)] border-[var(--border)]" data-testid="modal-quote-address" />
              </div>
              <div>
                <Label>Departamento * <span className="text-[10px] text-[var(--text-muted)]">(para análisis territorial)</span></Label>
                <select value={form.departamento} onChange={(e) => setForm({ ...form, departamento: e.target.value })}
                  className="w-full px-3 py-2 text-sm rounded-md bg-[var(--bg)] border border-[var(--border)] text-[var(--text)]"
                  data-testid="modal-quote-departamento">
                  <option value="">Selecciona un departamento...</option>
                  {GT_DEPARTAMENTOS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <Label>Notas</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Detalles, próximo paso..." className="bg-[var(--bg)] border-[var(--border)]" data-testid="modal-notes" />
              </div>
            </>
          )}

          {/* ─── SALE ─── */}
          {type === 'sale' && (
            <>
              <div>
                <Label>Cliente *</Label>
                <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Nombre del cliente" className="bg-[var(--bg)] border-[var(--border)]" autoFocus data-testid="modal-customer-name" />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} placeholder="+502 ..." className="bg-[var(--bg)] border-[var(--border)]" data-testid="modal-customer-phone" />
              </div>
              <div>
                <Label>Monto (Q) *</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" className="bg-[var(--bg)] border-[var(--border)]" data-testid="modal-amount" />
              </div>
              <div>
                <Label>Empresa de la venta *</Label>
                <div className="flex gap-2 mt-1">
                  <button type="button" onClick={() => setForm({ ...form, business_tag: 'CYH' })}
                    className={`flex-1 px-3 py-2 rounded-md border text-xs font-bold transition-colors ${form.business_tag === 'CYH' ? 'bg-[#1D4ED8] text-white border-[#1D4ED8]' : 'bg-[var(--surface)] text-[var(--text-muted)] border-[var(--border)] hover:bg-[var(--bg)]'}`}
                    data-testid="modal-business-cyh">🏢 CYH (Complementos)</button>
                  <button type="button" onClick={() => setForm({ ...form, business_tag: 'SA' })}
                    className={`flex-1 px-3 py-2 rounded-md border text-xs font-bold transition-colors ${form.business_tag === 'SA' ? 'bg-[#1D4ED8] text-white border-[#1D4ED8]' : 'bg-[var(--surface)] text-[var(--text-muted)] border-[var(--border)] hover:bg-[var(--bg)]'}`}
                    data-testid="modal-business-sa">🏛️ SA (Soluciones Arq.)</button>
                </div>
              </div>
              <div>
                <Label>Notas</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Detalles..." className="bg-[var(--bg)] border-[var(--border)]" data-testid="modal-notes" />
              </div>
            </>
          )}

        </form>

        <div className="flex gap-2 px-5 py-4 border-t border-[var(--border)] bg-[var(--surface)] shrink-0">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1 border-[var(--border)]" data-testid="modal-cancel">
            Cancelar
          </Button>
          <Button type="button" onClick={submit} disabled={saving || uploadingFile} className="flex-1 text-white font-bold" style={{ backgroundColor: meta.color }} data-testid="modal-save">
            {saving ? <CircleNotch size={16} className="animate-spin mr-2" /> : <CheckCircle size={16} weight="bold" className="mr-2" />}
            Guardar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const EditActivityModal = ({ activity, onClose, onSaved }) => {
  const meta = ACTIVITY_META[activity.type] || ACTIVITY_META.call;
  const hasAmount = activity.type === 'sale' || activity.type === 'quote';
  const [f, setF] = useState({
    customer_name: activity.customer_name || '',
    amount: activity.amount ?? '',
    notes: activity.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!f.customer_name.trim()) { toast.error('El nombre del cliente es requerido'); return; }
    if (hasAmount && (!f.amount || Number(f.amount) <= 0)) { toast.error('El monto debe ser mayor a 0'); return; }
    setSaving(true);
    try {
      const payload = { customer_name: f.customer_name.trim(), notes: f.notes.trim() || null };
      if (hasAmount) payload.amount = Number(f.amount);
      await api.patch(`/work-plan/activities/${activity.id}`, payload);
      toast.success('✓ Actividad actualizada — el cambio quedó registrado en la bitácora');
      onSaved();
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error('Tu sesión ha vencido. Inicia sesión nuevamente para continuar. Tus datos siguen en el formulario.');
      } else {
        toast.error(e?.response?.data?.detail || 'Error al guardar');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="modal-edit-activity">
        <DialogHeader>
          <DialogTitle>✏️ Editar {meta.label}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-[11px] text-[var(--text-muted)] bg-amber-500/10 border border-amber-500/30 rounded-md p-2">
            Solo puedes editar actividades del mes en curso. Todo cambio queda registrado con tu nombre.
          </p>
          <div>
            <Label className="text-xs font-bold">Cliente</Label>
            <Input value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} data-testid="edit-activity-customer" />
          </div>
          {hasAmount && (
            <div>
              <Label className="text-xs font-bold">Monto (Q)</Label>
              <Input type="number" min="0" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="edit-activity-amount" />
            </div>
          )}
          <div>
            <Label className="text-xs font-bold">Notas</Label>
            <Textarea rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="edit-activity-notes" />
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <Button variant="outline" onClick={onClose} data-testid="edit-activity-cancel">Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="edit-activity-save">
              {saving ? <CircleNotch size={16} className="animate-spin" /> : 'Guardar cambios'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MiDia;
